<template>
  <div class="sample-request-list">
    <div class="page-header">
      <h2>샘플요청 관리</h2>
      <div class="header-actions">
        <el-button
          v-if="userStore.isSampleManager"
          type="primary"
          @click="openCreateDialog"
        >
          <el-icon><Plus /></el-icon>
          신규 요청
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 필터 영역 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters">
        <el-form-item label="브랜드">
          <el-select v-model="filters.brand" filterable clearable placeholder="전체" style="width: 220px">
            <el-option
              v-for="brand in brands"
              :key="brand.id"
              :label="brand.managing_company_name ? `${brand.name} · ${brand.managing_company_name}` : brand.name"
              :value="brand.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="상태">
          <el-select v-model="filters.status" clearable placeholder="전체" style="width: 130px">
            <el-option label="요청" value="REQUESTED" />
            <el-option label="승인대기" value="PENDING_APPROVAL" />
            <el-option label="부서장승인" value="DEPT_APPROVED" />
            <el-option label="승인완료" value="APPROVED" />
            <el-option label="최종승인" value="FINAL_APPROVED" />
            <el-option label="작업중" value="IN_PROGRESS" />
            <el-option label="완료" value="COMPLETED" />
            <el-option label="반려" value="REJECTED" />
          </el-select>
        </el-form-item>
        <el-form-item label="샘플유형">
          <el-select v-model="filters.sample_type" clearable placeholder="전체" style="width: 130px">
            <el-option label="프로토" value="PROTO" />
            <el-option label="세일즈맨" value="SALESMAN" />
            <el-option label="PP샘플" value="PP" />
            <el-option label="TOP샘플" value="TOP" />
            <el-option label="AD샘플" value="AD" />
            <el-option label="기타" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="납기일">
          <el-date-picker
            v-model="filters.dueRange"
            type="daterange"
            range-separator="~"
            start-placeholder="시작일"
            end-placeholder="종료일"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchData">검색</el-button>
          <el-button @click="resetFilters">초기화</el-button>
          <el-button :type="showAll ? '' : 'warning'" @click="toggleShowAll" size="small">
            {{ showAll ? '스마트뷰' : '전체보기' }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 탭 영역 -->
    <el-tabs v-model="activeTab" @tab-click="handleTabClick">
      <el-tab-pane label="전체" name="all" />
      <el-tab-pane label="내 담당" name="mine" />
      <el-tab-pane label="승인대기" name="pending" />
      <el-tab-pane label="작업중" name="in_progress" />
      <el-tab-pane label="납기초과" name="overdue" />
    </el-tabs>

    <!-- 데이터 테이블 -->
    <el-card class="table-card">
      <el-table
        :data="sampleRequests || []"
        v-loading="loading"
        @row-click="handleRowClick"
        stripe
        highlight-current-row
        style="width: 100%"
      >
        <el-table-column prop="request_number" label="요청번호" width="140" fixed />
        <el-table-column prop="brand_name" label="브랜드" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.brand_name }}
            <span v-if="row.company_name" class="cell-sub">{{ row.company_name }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="design_name" label="디자인" min-width="160" show-overflow-tooltip />
        <el-table-column label="샘플유형" width="96">
          <template #default="{ row }">
            <el-tag size="small">{{ row.sample_type_display }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="상태" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ row.status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="회차" width="64" align="center">
          <template #default="{ row }">
            {{ row.current_round > 0 ? row.current_round + '차' : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="quantity" label="수량" width="76" align="right" />
        <el-table-column prop="due_date" label="납기일" width="108" />
        <el-table-column label="D-Day" width="78" align="center">
          <template #default="{ row }">
            <el-tag
              v-if="row.status !== 'COMPLETED' && row.status !== 'REJECTED'"
              :type="row.is_overdue ? 'danger' : row.days_remaining <= 3 ? 'warning' : 'info'"
              size="small"
            >
              {{ row.is_overdue ? 'D+' + Math.abs(row.days_remaining) : 'D-' + row.days_remaining }}
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="requester_name" label="요청자" width="92" />
        <el-table-column prop="request_date" label="요청일" width="108" />
        <el-table-column label="예상비용" width="110" align="right">
          <template #default="{ row }">
            {{ formatCurrency(row.estimated_cost) }}
          </template>
        </el-table-column>
        <el-table-column label="입력" width="70" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.is_auto" size="small" effect="dark" color="#1f3a5f" style="border:none"
                    :title="`자동 변환 — 원본: ${row.auto_origin?.source_file || ''}${row.auto_origin?.sheet_name ? ' / ' + row.auto_origin.sheet_name : ''}`">자동</el-tag>
            <span v-else style="color:#909399;font-size:11px">직접</span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </el-card>

    <!-- 생성 다이얼로그 -->
    <SampleRequestFormDialog
      v-model="showCreateDialog"
      @saved="handleSaved"
    />

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/sample-request-list'"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { Plus, QuestionFilled } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import SampleRequestFormDialog from '@/components/sample/SampleRequestFormDialog.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const router = useRouter()
const userStore = useUserStore()

const loading = ref(false)
const sampleRequests = ref([])
const brands = ref([])
const helpVisible = ref(false)
const showCreateDialog = ref(false)
const activeTab = ref('all')
const showAll = ref(false)

const filters = reactive({
  brand: null,
  status: null,
  sample_type: null,
  dueRange: null
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const getStatusType = (status) => {
  const types = {
    'REQUESTED': 'info',
    'PENDING_APPROVAL': 'warning',
    'DEPT_APPROVED': 'warning',
    'APPROVED': 'success',
    'FINAL_APPROVED': 'success',
    'IN_PROGRESS': '',
    'COMPLETED': 'success',
    'REJECTED': 'danger'
  }
  return types[status] || 'info'
}

const formatCurrency = (amount) => {
  if (!amount) return '-'
  return Number(amount).toLocaleString() + '원'
}

const fetchData = async () => {
  loading.value = true
  try {
    let url = 'sample-requests/'

    // 탭별 엔드포인트 분기
    if (activeTab.value === 'pending') {
      url = 'sample-requests/pending_approvals/'
    } else if (activeTab.value === 'in_progress') {
      url = 'sample-requests/in_progress/'
    } else if (activeTab.value === 'overdue') {
      url = 'sample-requests/overdue/'
    }

    const params = {
      page: pagination.page,
      page_size: pagination.pageSize,
    }

    if (activeTab.value === 'all' || activeTab.value === 'mine') {
      if (filters.brand) params.brand = filters.brand
      if (filters.status) params.status = filters.status
      if (filters.sample_type) params.sample_type = filters.sample_type
      if (filters.dueRange && filters.dueRange.length === 2) {
        params.due_from = filters.dueRange[0]
        params.due_to = filters.dueRange[1]
      }
      if (activeTab.value === 'mine') params.mine = 1

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (!params[key]) {
          delete params[key]
        }
      })

      // Smart filter: exclude COMPLETED, REJECTED when showAll is false
      if (!showAll.value && !params.status) {
        params.status__in = ['REQUESTED', 'PENDING_APPROVAL', 'DEPT_APPROVED', 'APPROVED', 'FINAL_APPROVED', 'IN_PROGRESS']
      }
    }

    const response = await axiosInstance.get(url, { params })
    sampleRequests.value = response.data.results || response.data
    pagination.total = response.data.count || sampleRequests.value.length
  } catch (error) {
    console.error('Failed to fetch sample requests:', error)
  } finally {
    loading.value = false
  }
}

const fetchBrands = async () => {
  try {
    const response = await axiosInstance.get('brands/')
    brands.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch brands:', error)
  }
}

const resetFilters = () => {
  filters.brand = null
  filters.status = null
  filters.sample_type = null
  filters.dueRange = null
  pagination.page = 1
  showAll.value = false
  fetchData()
}

const toggleShowAll = () => {
  showAll.value = !showAll.value
  pagination.page = 1
  fetchData()
}

const handleTabClick = () => {
  pagination.page = 1
  fetchData()
}

const handleRowClick = (row) => {
  router.push(`/sample-requests/${row.id}`)
}

const openCreateDialog = () => {
  showCreateDialog.value = true
}

const handleSaved = () => {
  showCreateDialog.value = false
  fetchData()
}

const showHelp = () => {
  helpVisible.value = true
}

onMounted(() => {
  fetchData()
  fetchBrands()
})
</script>

<style scoped>
.sample-request-list {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.el-table {
  cursor: pointer;
}

.el-pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

.el-tabs {
  margin-bottom: 15px;
}

/* 브랜드 셀 보조 표시(담당법인) */
.cell-sub {
  display: block;
  font-size: 11px;
  color: #909399;
  line-height: 1.2;
}
</style>
